import React, { useState, useEffect, useMemo, Fragment } from 'react';
import { generatePDF } from '../../utils/scenarioBuilder/pdfGenerator';
import { isCustomModule, renderCustomModule } from './CustomModules';

const PaginatedScenarioPreview = ({ template, visibleColumns = { item: true, objective: true, notes: true, score: true, value: true }, orientation = 'portrait' }) => {
  const [currentPage, setCurrentPage] = useState(1);
  
  // Calculate number of visible columns for header row colspan
  const visibleColumnCount = Object.values(visibleColumns).filter(Boolean).length;
  
  // Print page dimensions (matching print exactly)
  const pageStyles = orientation === 'portrait' 
    ? { 
        width: '8.5in', 
        height: '11in',
        maxWidth: '8.5in'
      }
    : { 
        width: '11in', 
        height: '8.5in',
        maxWidth: '11in'
      };

  // Calculate rows per page based on available space
  const getRowsPerPage = () => {
    // More conservative estimates based on actual content density
    if (orientation === 'portrait') {
      return 15; // Reduced for better accuracy
    } else {
      return 12; // Landscape has less vertical space
    }
  };

  // Split content into pages
  const pages = useMemo(() => {
    const rowsPerPage = getRowsPerPage();
    const pages = [];
    let currentPageRows = [];
    let currentPageNumber = 1;
    let rowCounter = 0;
    
    // Add header info to first page only
    const headerInfo = {
      name: template.name,
      scenarioInfo: {
        description: template.description,
        createdBy: template.createdBy,
        creationDate: template.creationDate
      },
      dispatchInfo: {
        dispatchScenario: template.dispatchScenario,
        dispatchTime: template.dispatchTime,
        location: template.location,
        natureOfCall: template.natureOfCall,
        weather: template.weather,
        personnelAtScene: template.personnelAtScene
      }
    };

    for (let i = 0; i < template.rows.length; i++) {
      const row = template.rows[i];
      
      // Headers and sub-headers don't count toward row limit
      if (row.type === 'header' || row.type === 'sub-header' || row.type === 'description' || row.type === 'blank-row') {
        currentPageRows.push({ ...row, originalIndex: i });
        continue;
      }
      
      // Regular content row
      rowCounter++;
      const adjustedRowNumber = rowCounter;
      
      // Check if we need a new page
      if (currentPageRows.filter(r => r.type !== 'header' && r.type !== 'sub-header').length >= rowsPerPage && currentPageNumber >= 1) {
        // Finish current page
        pages.push({
          pageNumber: currentPageNumber,
          rows: currentPageRows,
          headerInfo: currentPageNumber === 1 ? headerInfo : null
        });
        
        // Start new page
        currentPageNumber++;
        currentPageRows = [];
      }
      
      currentPageRows.push({ 
        ...row, 
        originalIndex: i, 
        rowNumber: adjustedRowNumber 
      });
    }
    
    // Add final page if it has content
    if (currentPageRows.length > 0) {
      pages.push({
        pageNumber: currentPageNumber,
        rows: currentPageRows,
        headerInfo: currentPageNumber === 1 ? headerInfo : null
      });
    }
    
    return pages;
  }, [template, orientation]);

  // Ensure current page is valid
  useEffect(() => {
    if (currentPage > pages.length) {
      setCurrentPage(1);
    }
  }, [pages.length, currentPage]);

  const currentPageData = pages[currentPage - 1] || pages[0];

  const renderPageContent = (pageData) => {
    if (!pageData) return null;

    return (
      <div 
        className="bg-white shadow-lg border border-gray-300 print-page"
        style={{
          ...pageStyles,
          padding: '0.75in',
          boxSizing: 'border-box',
          position: 'relative',
          overflow: 'hidden'
        }}
        id="scenario-preview-content"
      >
        {/* Header - only on first page */}
        {pageData.headerInfo && (
          <>
            <div className="text-center mb-4">
              <h1 className="text-2xl font-bold text-gray-800">{pageData.headerInfo.name}</h1>
            </div>

            {/* Info Grid */}
            <div className={`info-grid gap-4 mb-4 ${orientation === 'landscape' ? 'grid grid-cols-2' : 'grid grid-cols-1 md:grid-cols-2'}`}>
              {/* Scenario Info */}
              <div className="bg-gray-50 p-3 rounded-lg border">
                <h3 className="text-base font-bold text-gray-700 border-b pb-1 mb-2 text-center">Scenario Info</h3>
                <div className="space-y-2">
                  <div>
                    <p className="text-sm font-semibold text-gray-600">Description:</p>
                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{pageData.headerInfo.scenarioInfo.description || 'N/A'}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 pt-2">
                    <div>
                      <p className="text-sm font-semibold text-gray-600">Created by:</p>
                      <p className="text-sm text-gray-800">{pageData.headerInfo.scenarioInfo.createdBy || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-600">Creation Date:</p>
                      <p className="text-sm text-gray-800">{pageData.headerInfo.scenarioInfo.creationDate || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Dispatch Info */}
              <div className="bg-gray-50 p-3 rounded-lg border">
                <h3 className="text-base font-bold text-gray-700 border-b pb-1 mb-2 text-center">Dispatch Info</h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-600 mb-1">Dispatch Scenario:</p>
                    <div className="bg-white p-2 rounded border min-h-[60px]">
                      <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{pageData.headerInfo.dispatchInfo.dispatchScenario || 'N/A'}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-x-3 gap-y-2 text-xs">
                    <div>
                      <p className="font-semibold text-gray-600 mb-1">Time:</p>
                      <p className="text-gray-800 bg-white p-1 rounded">{pageData.headerInfo.dispatchInfo.dispatchTime || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-600 mb-1">Location:</p>
                      <p className="text-gray-800 bg-white p-1 rounded">{pageData.headerInfo.dispatchInfo.location || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-600 mb-1">Nature:</p>
                      <p className="text-gray-800 bg-white p-1 rounded">{pageData.headerInfo.dispatchInfo.natureOfCall || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-600 mb-1">Weather:</p>
                      <p className="text-gray-800 bg-white p-1 rounded">{pageData.headerInfo.dispatchInfo.weather || 'N/A'}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="font-semibold text-gray-600 mb-1">Personnel:</p>
                      <p className="text-gray-800 bg-white p-1 rounded">{pageData.headerInfo.dispatchInfo.personnelAtScene || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Table */}
        <div className="relative">
          <table className="w-full text-base border-collapse">
            <thead>
              <tr className="bg-gray-200 text-gray-800 border-b-2 border-gray-300">
                <th className="p-2 text-center font-semibold border border-gray-300" style={{ width: '2rem', verticalAlign: 'middle' }}>#</th>
                {visibleColumns.item && <th className="p-2 text-center text-sm font-bold text-gray-700 border border-gray-300" style={{ width: orientation === 'landscape' ? '15%' : '18%', verticalAlign: 'middle' }}>Item</th>}
                {visibleColumns.objective && <th className="p-2 text-center text-sm font-bold text-gray-700 border border-gray-300" style={{ width: orientation === 'landscape' ? '45%' : '40%', verticalAlign: 'middle' }}>Objective</th>}
                {visibleColumns.notes && <th className="p-2 text-center text-sm font-bold text-gray-700 border border-gray-300" style={{ verticalAlign: 'middle' }}>Instructor Notes</th>}
                {visibleColumns.score && <th className="p-2 text-center text-sm font-bold text-gray-700 border border-gray-300" style={{ width: '50px', verticalAlign: 'middle' }}>Score</th>}
                {visibleColumns.value && <th className="p-2 text-center text-sm font-bold text-gray-700 border border-gray-300" style={{ width: '50px', verticalAlign: 'middle' }}>Value</th>}
                <th className="p-2 text-center text-sm font-bold text-gray-700 border border-gray-300" style={{ width: '50px', verticalAlign: 'middle' }}>Critical</th>
              </tr>
            </thead>
            <tbody>
              {pageData.rows.map((row, idx) => {
                if (row.type === 'header') {
                  return (
                    <tr key={`${pageData.pageNumber}-${idx}`} className="bg-indigo-50">
                      <td colSpan={visibleColumnCount + 1} className="border border-gray-300 p-3 text-center font-bold text-lg text-indigo-800" style={{ verticalAlign: 'middle' }}>{row.text}</td>
                    </tr>
                  );
                }
                if (row.type === 'sub-header') {
                  // Check if this is a custom module sub-header
                  if (isCustomModule(row)) {
                    return (
                      <Fragment key={`${pageData.pageNumber}-${idx}`}>
                        {/* Sub-header row */}
                        <tr className="bg-purple-50">
                          <td colSpan={visibleColumnCount + 1} className="border border-gray-300 p-2 text-center font-semibold text-base text-purple-700" style={{ verticalAlign: 'middle' }}>
                            {row.text}
                          </td>
                        </tr>
                        {/* Module content row */}
                        <tr className="bg-white">
                          <td colSpan={visibleColumnCount + 1} className="border border-gray-300 p-0" style={{ verticalAlign: 'middle' }}>
                            {renderCustomModule(row, null, true)}
                          </td>
                        </tr>
                      </Fragment>
                    );
                  } else {
                    // Regular sub-header
                    const totalCols = visibleColumnCount + 1;
                    const subHeaderColspan = Math.ceil(totalCols / 2);
                    return (
                      <tr key={`${pageData.pageNumber}-${idx}`} className="bg-purple-50">
                        <td colSpan={subHeaderColspan} className="border border-gray-300 p-2 text-center font-semibold text-base text-purple-700" style={{ verticalAlign: 'middle' }}>{row.text}</td>
                      </tr>
                    );
                  }
                }
                
                if (row.type === 'description') {
                  return (
                    <tr key={`${pageData.pageNumber}-${idx}`} className="bg-amber-50">
                      <td colSpan={visibleColumnCount + 1} className="border border-gray-300 p-3 text-center italic text-base text-amber-800" style={{ verticalAlign: 'middle' }}>{row.text}</td>
                    </tr>
                  );
                }
                
                if (row.type === 'blank-row') {
                  return (
                    <tr key={`${pageData.pageNumber}-${idx}`} className="bg-slate-50">
                      <td className="border border-gray-300 p-2 text-center font-semibold text-slate-700" style={{ verticalAlign: 'middle' }}>-</td>
                      {visibleColumns.item && <td className={`p-2 whitespace-pre-wrap break-words font-bold ${(visibleColumns.objective || visibleColumns.notes) ? 'border border-gray-300' : 'border-t border-b border-l border-gray-300'}`} style={{ verticalAlign: 'middle' }}>{row.item}</td>}
                      {visibleColumns.objective && <td className={`p-2 whitespace-pre-wrap break-words ${visibleColumns.notes ? 'border border-gray-300' : 'border-t border-b border-l border-gray-300'}`} style={{ verticalAlign: 'middle' }}>{row.objective}</td>}
                      {visibleColumns.notes && <td className="border-t border-b border-l border-gray-300 p-2 whitespace-pre-wrap break-words" style={{ verticalAlign: 'middle' }}>{row.notes}</td>}
                      {visibleColumns.score && <td className="p-2 text-center bg-gray-100" style={{ verticalAlign: 'middle' }}></td>}
                      {visibleColumns.value && <td className="p-2 text-center bg-gray-100" style={{ verticalAlign: 'middle' }}></td>}
                      <td className="border-t border-b border-r border-gray-300 p-2 text-center bg-gray-100" style={{ verticalAlign: 'middle' }}></td>
                    </tr>
                  );
                }
                
                // Skip custom modules that are sub-headers - they're already handled above
                if (isCustomModule(row) && row.type === 'sub-header') {
                  return null;
                }
                
                const rowNumber = row.rowNumber;
                return (
                  <tr key={`${pageData.pageNumber}-${idx}`} className={rowNumber % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="text-center p-2 border border-gray-300 font-semibold text-gray-700" style={{ verticalAlign: 'middle' }}>{rowNumber}</td>
                    {visibleColumns.item && <td className="border border-gray-300 p-2 whitespace-pre-wrap break-words font-bold" style={{ verticalAlign: 'middle' }}>{row.item}</td>}
                    {visibleColumns.objective && (
                      <td className="border border-gray-300 p-2 whitespace-pre-wrap break-words" style={{ verticalAlign: 'middle' }}>
                        {isCustomModule(row) ? (
                          renderCustomModule(row, null, true)
                        ) : (
                          row.objective
                        )}
                      </td>
                    )}
                    {visibleColumns.notes && <td className="border border-gray-300 p-2 whitespace-pre-wrap break-words" style={{ verticalAlign: 'middle' }}>{row.notes}</td>}
                    {visibleColumns.score && <td className="border border-gray-300 p-2 text-center" style={{ verticalAlign: 'middle' }}>{row.score || ''}</td>}
                    {visibleColumns.value && <td className="border border-gray-300 p-2 text-center" style={{ verticalAlign: 'middle' }}>{row.value || ''}</td>}
                    <td className="border border-gray-300 p-2 text-center" style={{ verticalAlign: 'middle' }}>
                      {row.critical ? <span className="text-red-600 font-bold critical-icon" title="Critical">⚠</span> : ''}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Page number at bottom */}
        <div className="absolute bottom-4 right-4 text-sm text-gray-500">
          Page {pageData.pageNumber} of {pages.length}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-gray-100 p-4">
      {/* Controls */}
      <div className="flex justify-center items-center gap-4 mb-4 print-controls">
        {/* Pagination */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 bg-gray-200 text-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300"
          >
            ← Previous
          </button>
          
          <span className="mx-3 text-sm font-medium">
            Page {currentPage} of {pages.length}
          </span>
          
          <button
            onClick={() => setCurrentPage(Math.min(pages.length, currentPage + 1))}
            disabled={currentPage === pages.length}
            className="px-3 py-1 bg-gray-200 text-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300"
          >
            Next →
          </button>
        </div>

        {/* Print Button */}
        <button
          onClick={() => generatePDF(template, orientation)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          title="Print/Save as PDF"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a2 2 0 002 2h6a2 2 0 002-2v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a2 2 0 00-2-2H7a2 2 0 00-2 2zm8 0H7v3h6V4zm0 8H7v4h6v-4z" clipRule="evenodd" />
          </svg>
          Print/PDF
        </button>
      </div>

      {/* Page Container */}
      <div className="flex justify-center">
        {renderPageContent(currentPageData)}
      </div>
    </div>
  );
};

export default PaginatedScenarioPreview;
